// Mock data for the "Community Projects" feature (TBBR-58).
// FRONTEND-ONLY / DEMO DATA — no backend, no API. Swap this content freely
// without touching the components; the components read only the shapes below.

// Project status values. Only ONE project may be "ongoing" at any time.
export const COMMUNITY_PROJECT_STATUSES = {
  ONGOING: "ongoing",
  ON_HOLD: "on_hold",
  REACHED_GOAL: "reached_goal",
  COMPLETED: "completed",
};

// Project type values.
export const PROJECT_TYPES = {
  IN_SYSTEM: "in_system",
  MANAGER_DONATION: "manager_donation",
};

// Demo images (real photos, language-agnostic), served locally from
// public/mock-projects so they always load (no network/CDN dependency).
// Swap these files / paths for finalized demo art when ready.
const IMAGES = {
  garden: {
    main: "/mock-projects/garden.jpg",
    extra: ["/mock-projects/garden-a.jpg", "/mock-projects/garden-b.jpg"],
  },
  center: {
    main: "/mock-projects/center.jpg",
    extra: ["/mock-projects/center-a.jpg"],
  },
  holiday: {
    main: "/mock-projects/holiday.jpg",
    extra: [],
  },
  fund: {
    main: "/mock-projects/fund.jpg",
    extra: ["/mock-projects/fund-a.jpg"],
  },
};

/**
 * A community project (demo shape — typed by comment, not TypeScript):
 * {
 *   id: string,
 *   name: string,
 *   type: PROJECT_TYPES[keyof],
 *   description: string,
 *   terms: string,
 *   fundingGoal: number,
 *   raisedAmount: number,
 *   status: COMMUNITY_PROJECT_STATUSES[keyof],
 *   mainImage: string (url),
 *   extraImages: string[] (urls)
 * }
 *
 * Demo content comes in two languages so the Israeli (Hebrew/RTL) demo reads
 * natively while the English UI still looks right. The component picks the set
 * matching the active i18next language via `communityProjectsMockByLang`.
 */

const projectsEn = [
  {
    id: "proj-1",
    name: "Fixing up the community garden",
    type: PROJECT_TYPES.IN_SYSTEM,
    description:
      "Build a safe, modern playground and refresh the shared garden for the children of our community.",
    terms:
      "Funds are used solely for purchasing and installing playground and garden equipment.",
    fundingGoal: 500000,
    raisedAmount: 157082,
    status: COMMUNITY_PROJECT_STATUSES.ONGOING,
    mainImage: IMAGES.garden.main,
    extraImages: IMAGES.garden.extra,
  },
  {
    id: "proj-2",
    name: "Community Center Renovation",
    type: PROJECT_TYPES.IN_SYSTEM,
    description:
      "Renovate the shared community center to host more events and activities.",
    terms: "Includes flooring, paint, and new furniture for the main hall.",
    fundingGoal: 1000000,
    raisedAmount: 296002,
    status: COMMUNITY_PROJECT_STATUSES.ON_HOLD,
    mainImage: IMAGES.center.main,
    extraImages: IMAGES.center.extra,
  },
  {
    id: "proj-3",
    name: "Annual Holiday Fund",
    type: PROJECT_TYPES.MANAGER_DONATION,
    description:
      "Support families in need during the holiday season with gift baskets.",
    terms: "Distributed by the community manager to families across Keilot.",
    fundingGoal: 200000,
    raisedAmount: 200000,
    status: COMMUNITY_PROJECT_STATUSES.REACHED_GOAL,
    mainImage: IMAGES.holiday.main,
    extraImages: IMAGES.holiday.extra,
  },
  {
    id: "proj-4",
    name: "Community Fund For Bad Days",
    type: PROJECT_TYPES.IN_SYSTEM,
    description:
      "Create a shared emergency fund to support members through hard times.",
    terms: "Distributed to community members in need, decided by the manager.",
    fundingGoal: 80000,
    raisedAmount: 0,
    status: COMMUNITY_PROJECT_STATUSES.COMPLETED,
    mainImage: IMAGES.fund.main,
    extraImages: IMAGES.fund.extra,
  },
];

const projectsHe = [
  {
    id: "proj-1",
    name: "שיפוץ הגינה הקהילתית",
    type: PROJECT_TYPES.IN_SYSTEM,
    description:
      "הקמת מגרש משחקים בטוח ומודרני וחידוש הגינה המשותפת עבור ילדי הקהילה.",
    terms: "הכספים משמשים אך ורק לרכישה והתקנה של מתקני משחק וציוד לגינה.",
    fundingGoal: 500000,
    raisedAmount: 157082,
    status: COMMUNITY_PROJECT_STATUSES.ONGOING,
    mainImage: IMAGES.garden.main,
    extraImages: IMAGES.garden.extra,
  },
  {
    id: "proj-2",
    name: "שיפוץ מרכז הקהילה",
    type: PROJECT_TYPES.IN_SYSTEM,
    description: "שיפוץ מרכז הקהילה המשותף כדי לארח יותר אירועים ופעילויות.",
    terms: "כולל ריצוף, צביעה וריהוט חדש לאולם המרכזי.",
    fundingGoal: 1000000,
    raisedAmount: 296002,
    status: COMMUNITY_PROJECT_STATUSES.ON_HOLD,
    mainImage: IMAGES.center.main,
    extraImages: IMAGES.center.extra,
  },
  {
    id: "proj-3",
    name: "קרן חגים שנתית",
    type: PROJECT_TYPES.MANAGER_DONATION,
    description: "תמיכה במשפחות נזקקות בתקופת החגים באמצעות סלי מתנה.",
    terms: "מחולק על ידי מנהל הקהילה למשפחות ברחבי קיילות.",
    fundingGoal: 200000,
    raisedAmount: 200000,
    status: COMMUNITY_PROJECT_STATUSES.REACHED_GOAL,
    mainImage: IMAGES.holiday.main,
    extraImages: IMAGES.holiday.extra,
  },
  {
    id: "proj-4",
    name: "קרן קהילתית לימים קשים",
    type: PROJECT_TYPES.IN_SYSTEM,
    description: "הקמת קרן חירום משותפת לתמיכה בחברי הקהילה בתקופות קשות.",
    terms: "מחולק לחברי הקהילה הנזקקים, על פי החלטת מנהל הקהילה.",
    fundingGoal: 80000,
    raisedAmount: 0,
    status: COMMUNITY_PROJECT_STATUSES.COMPLETED,
    mainImage: IMAGES.fund.main,
    extraImages: IMAGES.fund.extra,
  },
];

// Locale -> seed projects. Components select the set for the active language.
export const communityProjectsMockByLang = {
  en: projectsEn,
  he: projectsHe,
};

// Default to Hebrew content for the Israeli demo.
export const communityProjectsMock = projectsHe;

export default communityProjectsMock;
